/**
 * VHAKACHA ID GENERATION
 * ============================================================
 * Replaces `uid(32)` from the `uid` package, which is seeded from
 * Math.random(). That produced observably non-independent IDs — a
 * booking and an itinerary created in the same session shared a
 * 31-of-32 character core:
 *
 *   itinerary  8 + 4163fe5e4274ce4f0466df70402aaad
 *   booking        4163fe5e4274ce4f0466df70402aaad + 7
 *
 * Two independent 32-hex IDs colliding on 31 characters is not
 * chance. Since document IDs are also the collision domain for
 * `setDoc`, a duplicate silently OVERWRITES an existing booking
 * rather than erroring.
 *
 * These helpers use the CSPRNG via crypto.getRandomValues, with a
 * documented degraded fallback only for insecure contexts.
 * ============================================================
 */

// Resolved off `window` only. CRA 5's eslint config rejects `globalThis`
// (no-undef) and `self` (no-restricted-globals), and every caller here runs
// on the page rather than inside the service worker.
function getCrypto() {
  if (typeof window !== 'undefined' && window.crypto) return window.crypto;
  return null;
}

/**
 * RFC-4122 v4 UUID. Matches the provider app's `generateUUID()` so both
 * apps mint IDs from the same alphabet and length.
 */
export function generateUUID() {
  const c = getCrypto();

  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Last resort: no Web Crypto (non-HTTPS, ancient browser). Still far
  // better entropy than uid(32), but log it — this path should not run
  // in production, where the PWA is served over HTTPS.
  console.warn('[vhakacha/ids] Web Crypto unavailable; falling back to Math.random for ID generation.');
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Alias used at booking/itinerary creation sites. */
export const newBookingId = generateUUID;
export const newItineraryId = generateUUID;
